import {
  useState,
  useEffect,
  useRef,
  type ReactNode,
  type FormEvent,
  type RefObject,
  createContext,
  useContext,
} from "react";
import { Link, redirect, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useDebouncer } from "@tanstack/react-pacer";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { Loader2, Save, X } from "lucide-react";
import { Button } from "@carry/ui";
import { TablePagination } from "../components/table-pagination";
import type { RouterContext } from "../router-context";
import { formatFieldErrors } from "../components/order-form/primitives";
import { SearchInput } from "../components/search-input";
import { TableSkeletonRows } from "../components/table-skeleton";
import { useAuth } from "../providers/auth-context";
import { getVisibleMasterMenuItems, type MasterMenuKey } from "../lib/master-menu";

export function MastersShell({
  tab: _tab,
  onCreate,
  createLabel = "+ 新規追加",
  searchPlaceholder = "名称で検索",
  children,
}: {
  tab: MasterMenuKey;
  onCreate?: () => void;
  createLabel?: string;
  searchPlaceholder?: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const q = useSearch({
    strict: false,
    select: (search) => (typeof search.q === "string" ? search.q : ""),
  });
  const [localQ, setLocalQ] = useState(q);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { session } = useAuth();
  const visibleTabs = getVisibleMasterMenuItems(session?.user?.role);

  useEffect(() => {
    setLocalQ(q);
  }, [q]);

  const debouncer = useDebouncer(
    (value: string) =>
      navigate({ to: ".", search: (prev) => ({ ...prev, q: value || undefined, page: 1 }) }),
    { wait: 300 },
    (state) => ({ isPending: state.isPending }),
  );

  const handleQChange = (value: string) => {
    setLocalQ(value);
    debouncer.maybeExecute(value);
  };

  return (
    <div style={{ margin: -24 }}>
      <div
        className="cp-pageheader"
        style={{
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          gap: 24,
          borderBottom: "0.5px solid var(--rule-softer)",
          background: "var(--bg)",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button type="button" variant="default" size="sm" disabled={!onCreate} onClick={onCreate}>
            {createLabel}
          </Button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "0.5px solid var(--rule)",
          padding: "0 32px",
          background: "var(--bg-panel)",
        }}
      >
        {visibleTabs.map((m) => {
          const active = pathname === m.path;
          return (
            <Link
              key={m.key}
              to={m.path}
              search={(prev) => {
                const p = prev as { q?: string; page?: number; pageSize?: number };
                return { q: p.q ?? "", page: p.page ?? 1, pageSize: 50 };
              }}
              style={{
                padding: "10px 16px",
                fontSize: 12,
                color: active ? "var(--ink)" : "var(--ink-3)",
                fontWeight: active ? 500 : 400,
                borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
                marginBottom: -1,
                textDecoration: "none",
              }}
            >
              {m.tabLabel}
            </Link>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 32px",
          borderBottom: "0.5px solid var(--rule-soft)",
          background: "var(--bg)",
        }}
      >
        <SearchInput
          value={localQ}
          onChange={handleQChange}
          placeholder={searchPlaceholder}
          pending={debouncer.state.isPending}
          ariaLabel="マスタを検索"
        />
        <span style={{ flex: 1 }} />
      </div>

      <div style={{ padding: "0 32px 80px" }}>
        <MastersContext.Provider value={{ q }}>{children}</MastersContext.Provider>
      </div>
    </div>
  );
}

const MastersContext = createContext<{ q: string }>({ q: "" });
export function useMastersQ(): string {
  return useContext(MastersContext).q;
}

const DIALOG_FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function useDialogFocusTrap<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  initialFocusSelector?: string,
) {
  useEffect(() => {
    const currentContainer = containerRef.current;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!currentContainer) return;
    const container: T = currentContainer;

    const getFocusableElements = () =>
      Array.from(container.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTOR));
    const initialFocus = initialFocusSelector
      ? container.querySelector<HTMLElement>(initialFocusSelector)
      : getFocusableElements()[0];
    (initialFocus ?? container).focus();

    function keepFocusInside(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements.at(-1);
      const activeElement = document.activeElement;
      const activeIndex =
        activeElement instanceof HTMLElement ? focusableElements.indexOf(activeElement) : -1;
      if (activeIndex === -1) {
        e.preventDefault();
        (e.shiftKey ? last : first)?.focus();
      } else if (e.shiftKey && activeIndex === 0) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && activeIndex === focusableElements.length - 1) {
        e.preventDefault();
        first?.focus();
      }
    }

    window.addEventListener("keydown", keepFocusInside);
    return () => {
      window.removeEventListener("keydown", keepFocusInside);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [containerRef, initialFocusSelector]);
}

export function FormDialog({
  title,
  onClose,
  onSubmit,
  submitting = false,
  submitLabel = "保存",
  submitIcon,
  error,
  disableSubmit = false,
  hideSubmit = false,
  width,
  maxWidth,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  submitting?: boolean;
  submitLabel?: string;
  submitIcon?: ReactNode;
  error?: string | null;
  disableSubmit?: boolean;
  hideSubmit?: boolean;
  width?: number | string;
  maxWidth?: number | string;
  children: ReactNode;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  useDialogFocusTrap(
    formRef,
    "input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])",
  );
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="form-dialog-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
      }}
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <form
        ref={formRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        style={{
          background: "var(--bg-panel)",
          border: "0.5px solid var(--rule)",
          borderRadius: 3,
          minWidth: 500,
          width,
          maxWidth: maxWidth ?? 720,
          maxHeight: "90vh",
          overflow: "hidden",
          fontSize: 13,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "16px 20px",
            borderBottom: "0.5px solid var(--rule-softer)",
            background: "var(--bg-panel)",
          }}
        >
          <h2 id="form-dialog-title" style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
            {title}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="閉じる"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={16} />
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: 20,
            overflowY: "auto",
            minHeight: 0,
            flex: 1,
          }}
        >
          {children}
          {error && (
            <p
              role="alert"
              // 検証エラーは項目ごとに改行で並ぶため、改行を保持して表示する (#672)。
              style={{
                fontSize: 11,
                color: "var(--danger)",
                margin: 0,
                whiteSpace: "pre-line",
              }}
            >
              {error}
            </p>
          )}
        </div>
        <div
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 1,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            padding: "12px 20px",
            borderTop: "0.5px solid var(--rule-softer)",
            background: "var(--bg-panel)",
          }}
        >
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            <X size={14} />
            キャンセル
          </Button>
          {!hideSubmit && (
            <Button
              type="submit"
              variant="default"
              size="sm"
              disabled={submitting || disableSubmit}
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                (submitIcon ?? <Save size={14} />)
              )}
              {submitLabel}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

export function Field({
  label,
  required,
  hint,
  errors,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  hint?: string;
  errors?: ReadonlyArray<unknown>;
  children: ReactNode;
}) {
  const errorMessage = formatFieldErrors(errors);
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
        {label}
        {required && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: 9,
              fontWeight: 700,
              color: "oklch(0.45 0.2 30)",
              background: "oklch(0.97 0.03 30)",
              border: "0.5px solid oklch(0.75 0.12 30)",
              borderRadius: 3,
              padding: "1px 5px",
              letterSpacing: "0.06em",
              lineHeight: "14px",
              marginLeft: 4,
            }}
          >
            必須
          </span>
        )}
      </span>
      {children}
      {errorMessage && (
        <span role="alert" style={{ fontSize: 10, color: "var(--danger)", fontWeight: 500 }}>
          {errorMessage}
        </span>
      )}
      {hint && !errorMessage && <span style={{ fontSize: 10, color: "var(--ink-4)" }}>{hint}</span>}
    </label>
  );
}

// ポータル方式の共有 HelpTooltip に一本化 (モーダルの overflow クリップ対策)。
// 既存 import 経路 (masters.clients 等が "./_masters" から取得) を維持するため再エクスポートする。
export { HelpTooltip } from "../components/help-tooltip";

export function ConfirmDeleteDialog({
  title,
  message,
  onCancel,
  onConfirm,
  submitting = false,
  error,
}: {
  title: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  submitting?: boolean;
  error?: string | null;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocusTrap(dialogRef, '[data-dialog-initial-focus="true"]');
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, submitting]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-dialog-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
      }}
      onClick={() => {
        if (!submitting) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          border: "0.5px solid var(--rule)",
          borderRadius: 3,
          padding: 20,
          minWidth: 420,
          maxWidth: 540,
          fontSize: 13,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 id="confirm-delete-dialog-title" style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
            {title}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="閉じる"
            onClick={onCancel}
            disabled={submitting}
          >
            <X size={16} />
          </Button>
        </div>
        <p style={{ fontSize: 12, color: "var(--ink-2)", margin: 0, whiteSpace: "pre-wrap" }}>
          {message}
        </p>
        {error && <p style={{ fontSize: 11, color: "var(--danger)", margin: 0 }}>{error}</p>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-dialog-initial-focus="true"
            onClick={onCancel}
            disabled={submitting}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onConfirm}
            disabled={submitting}
            style={{ background: "var(--danger)", color: "#fff" }}
          >
            {submitting ? "削除中…" : "削除"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface MastersSearch {
  q?: string;
  page: number;
  pageSize: number;
  edit?: string;
}

export function mastersValidateSearch(search: Record<string, unknown>): MastersSearch {
  return {
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    page: typeof search.page === "number" ? search.page : 1,
    pageSize: 50,
    edit: typeof search.edit === "string" && search.edit ? search.edit : undefined,
  };
}

export function mastersBeforeLoad({ context }: { context: RouterContext }) {
  if (context.authLoading) return;
  if (!context.session) throw redirect({ to: "/auth/login" });
}

export function MastersDataTable<T>({
  rows,
  columns,
  isLoading,
  error,
  emptyMessage,
  search,
}: {
  rows: T[];
  columns: ColumnDef<T>[];
  isLoading: boolean;
  error: unknown;
  emptyMessage: string;
  search: MastersSearch;
}) {
  const navigate = useNavigate();
  const { page, pageSize } = search;

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex: page - 1, pageSize } },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater({ pageIndex: page - 1, pageSize }) : updater;
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, page: next.pageIndex + 1, pageSize: next.pageSize }),
      });
    },
  });

  return (
    <>
      {error != null && (
        <div
          role="alert"
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            background: "oklch(0.96 0.05 30)",
            border: "0.5px solid var(--danger)",
            color: "var(--danger)",
            fontSize: 12,
          }}
        >
          取得に失敗しました: {String(error)}
        </div>
      )}
      <table className="tbl" style={{ fontSize: 12, marginTop: 4 }}>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  style={h.column.columnDef.size ? { width: h.column.columnDef.size } : undefined}
                >
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading && <TableSkeletonRows rows={6} columns={columns.length} />}
          {!isLoading && table.getRowModel().rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}
              >
                {emptyMessage}
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <TablePagination table={table} />
    </>
  );
}

export function RowActionButtons({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
      {onEdit && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          style={{ fontSize: 11, padding: "2px 8px" }}
        >
          編集
        </Button>
      )}
      {onDelete && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{ fontSize: 11, padding: "2px 8px", color: "var(--danger)" }}
        >
          削除
        </Button>
      )}
    </div>
  );
}
